import {
  mockStorefrontAPIClient,
  storefrontAPIClient,
} from 'utilities/storefront-api-client';
import {isThemeEditor} from 'utilities/common';
import {trackDeliveryPromiseFetching} from 'utilities/tracking';

import {
  APISubscriberCallbackFns,
  DeliveryEstimates,
  DeliveryEstimatesData,
  DeliveryEstimatesRequestParameters,
  DeliveryPromiseAPIClient,
  QueryMetadata,
} from '../types';
import {NoDeliveryEstimateError} from '../errors';

export const CACHE_TTL = 1000 * 60 * 5;
export const cachedResponse = new Map<
  string,
  {
    ttl: string;
    result: (DeliveryEstimates & QueryMetadata) | Error;
  }
>();
export const forceDisplayDates = new URLSearchParams(location.search).has(
  'forceDisplayDates',
);
export const inFlight = new Set<string>();
export const subscribers = new Map<string, APISubscriberCallbackFns[]>();

export const createDeliveryEstimateKey = (...props: (string | number)[]) =>
  props.join('$');

export function deliveryPromiseAPIClient(): DeliveryPromiseAPIClient {
  let runQuery = storefrontAPIClient;
  const mockRunQuery = mockStorefrontAPIClient;

  if (isThemeEditor() || forceDisplayDates) {
    runQuery = mockRunQuery;
  }

  return async (
    {
      variantId,
      sellingPlanId,
      postalCode = '',
      defaultPostalCode = '',
    }: DeliveryEstimatesRequestParameters,
    storefrontAPIAccessToken: string,
  ) => {
    const deliveryEstimateCacheKey = createDeliveryEstimateKey(
      variantId,
      sellingPlanId ?? '',
      postalCode ?? '',
      defaultPostalCode,
    );

    if (cachedResponse.has(deliveryEstimateCacheKey)) {
      const cachedResult = cachedResponse.get(deliveryEstimateCacheKey);
      const currentTime = new Date();

      if (cachedResult && new Date(cachedResult.ttl) > currentTime) {
        if (cachedResult.result instanceof Error) {
          throw cachedResult.result;
        }
        return cachedResult.result;
      } else if (cachedResult && new Date(cachedResult.ttl) < currentTime) {
        cachedResponse.delete(deliveryEstimateCacheKey);
      }
    }

    if (inFlight.has(deliveryEstimateCacheKey)) {
      return waitForInFlightRequest(deliveryEstimateCacheKey);
    }

    inFlight.add(deliveryEstimateCacheKey);
    const deliveryEstimatesQuery = buildDeliveryEstimatesQuery(
      variantId,
      sellingPlanId,
      postalCode || defaultPostalCode,
      Boolean(postalCode),
    );

    try {
      trackDeliveryPromiseFetching();

      const {data, headers} = await runQuery<DeliveryEstimatesData>(
        deliveryEstimatesQuery,
        storefrontAPIAccessToken,
      );

      if (!data.deliveryEstimates) {
        const requestId = headers?.get('X-Request-Id');
        const noDeliveryEstimateError = new NoDeliveryEstimateError(
          requestId ?? '',
        );
        cachedResponse.set(deliveryEstimateCacheKey, {
          ttl: getTTL(null),
          result: noDeliveryEstimateError,
        });
        throw noDeliveryEstimateError;
      }

      const result = {
        ...data.deliveryEstimates,
        datacenter: headers?.get('X-Dc'),
        shardId: headers?.get('X-Shardid'),
        requestId: headers?.get('X-Request-Id'),
      } as DeliveryEstimates & QueryMetadata;

      cachedResponse.set(deliveryEstimateCacheKey, {
        ttl: getTTL(data.deliveryEstimates),
        result,
      });

      subscribers.get(deliveryEstimateCacheKey)?.forEach(({onSuccess}) => {
        onSuccess(result);
      });
      return result;
    } catch (error) {
      subscribers.get(deliveryEstimateCacheKey)?.forEach(({onFail}) => {
        onFail(error);
      });
      throw error;
    } finally {
      inFlight.delete(deliveryEstimateCacheKey);
      subscribers.delete(deliveryEstimateCacheKey);
    }
  };
}

export function buildDeliveryEstimatesQuery(
  variantId: number,
  sellingPlanId?: number | null,
  postalCode?: string,
  isPostalCodeOverride = false,
  // todo: fix typing
): string {
  const variables = {
    productVariantId: `"gid://shopify/ProductVariant/${variantId}"`,
    ...(postalCode
      ? {
          countryCode: 'US',
          postalCode: `"${postalCode}"`,
          isPostalCodeOverride,
        }
      : {}),
    ...(sellingPlanId ? {sellingPlanId} : {}),
  };
  const variablesStringified = Object.entries(variables)
    .map(([key, value]) => `${key}: ${value}`)
    .join(',');

  return `query {
        deliveryEstimates(
          ${variablesStringified}
        ) {
          options {
            deliveryPromisePresentmentTitle
            minDeliveryTime
            maxDeliveryTime
            minCalendarDaysToDelivery
            maxCalendarDaysToDelivery
            expiresAt
            cost {
              amount
            }
          }

          selectedShippingOption {
            deliveryPromisePresentmentTitle
            minDeliveryTime
            maxDeliveryTime
            minCalendarDaysToDelivery
            maxCalendarDaysToDelivery
            expiresAt
            reliableDateSource
            cost {
              amount
            }
            brandedPromise {
              name
              handle
              lightLogo: compactLogo(theme: LIGHT) {
                src
                altText
              }
              darkLogo: compactLogo(theme: DARK) {
                src
                altText
              }
            }
            deliveryPromiseProviderAppId
          }

          deliveryAddress {
            zip
            city
            province
            provinceCode
            country
            countryCodeV2
            timezone
          }
          productHandle
          deliveryDateMissingReason
          variant
          product
        }
      }`;
}

function waitForInFlightRequest(
  cacheKey: string,
): Promise<DeliveryEstimates & QueryMetadata> {
  const promise = new Promise<DeliveryEstimates & QueryMetadata>(
    (resolve, reject) => {
      if (subscribers.has(cacheKey)) {
        subscribers.get(cacheKey)?.push({onSuccess: resolve, onFail: reject});
      } else {
        subscribers.set(cacheKey, [{onSuccess: resolve, onFail: reject}]);
      }
    },
  );
  return promise;
}

export function getTTL(
  deliveryEstimates: DeliveryEstimates | null,
  ttl = CACHE_TTL,
): string {
  const expiresAt = deliveryEstimates?.selectedShippingOption?.expiresAt;

  return expiresAt ? expiresAt : new Date(Date.now() + ttl).toISOString();
}
